import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, type MantineStyleProp, Table as MantineTable } from '@mantine/core';
import type { Header, RowData } from '@tanstack/react-table';
import React from 'react';

import { DragIcon } from '@apple/assets/icons';

import type { DataTableFeatures } from '../features';
import classes from './draggable-column.module.css';

export function useColumnDnD({
	columnOrder,
	setColumnOrder,
}: {
	columnOrder: string[];
	setColumnOrder?: React.Dispatch<React.SetStateAction<string[]>>;
}) {
	const sensors = useSensors(
		useSensor(MouseSensor, {}),
		useSensor(TouchSensor, {}),
		useSensor(KeyboardSensor, {}),
	);

	if (!setColumnOrder) {
		return {
			DndProvider: React.Fragment,
			SortableWrapper: React.Fragment,
			SortableTh,
		};
	}

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;
		setColumnOrder(old =>
			arrayMove(old, old.indexOf(active.id as string), old.indexOf(over.id as string)),
		);
	};

	function DndProvider({ children }: { children: React.ReactNode }) {
		return (
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
				{children}
			</DndContext>
		);
	}

	function SortableWrapper({ children }: { children: React.ReactNode }) {
		return (
			<SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
				{children}
			</SortableContext>
		);
	}

	function SortableTh<T extends RowData>({
		column,
		width,
		children,
		...rest
	}: {
		column: Header<DataTableFeatures, T, unknown>;
		width: number;
		children: React.ReactNode;
	} & React.ComponentProps<typeof MantineTable.Th>) {
		const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
			useSortable({
				id: column.column.id,
			});

		const mergedStyle: MantineStyleProp = {
			...(rest.style ?? {}),
			transform: CSS.Translate.toString(transform),
			transition,
			opacity: isDragging ? 0.4 : undefined,
		};

		return (
			<MantineTable.Th
				ref={setNodeRef}
				w={width}
				pos='relative'
				{...rest}
				style={mergedStyle}
			>
				<Button
					{...attributes}
					{...listeners}
					size='compact-sm'
					variant='transparent'
					classNames={{ root: classes.root, inner: classes.icon }}
					aria-label='Reorder column'
				>
					<DragIcon />
				</Button>
				{children}
			</MantineTable.Th>
		);
	}

	return { DndProvider, SortableWrapper, SortableTh };
}
